import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Shield, Users, Settings, FileText, LogOut, Home, BarChart3, Bell } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import AdminDashboard from '../components/admin/AdminDashboard';
import AdminSitemap from '../components/admin/AdminSitemap';
import AdminReleases from '../components/admin/AdminReleases';
import AdminUsers from '../components/admin/AdminUsers';
import AdminConfig from '../components/admin/AdminConfig';

const API = `${window.location.origin}/api`;

export default function Admin() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');

  const [users, setUsers] = useState([]);
  const [releases, setReleases] = useState([]);
  const [siteConfig, setSiteConfig] = useState(null);
  const [sitemap, setSitemap] = useState(null);
  const [stats, setStats] = useState({});

  const [newRelease, setNewRelease] = useState({ version: '', title: '', description: '' });
  const [configForm, setConfigForm] = useState({});
  const [newUser, setNewUser] = useState({ email: '', password: '', first_name: '', last_name: '', role: 'adherent', phone: '' });
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  const getToken = () => localStorage.getItem('briouso_token');
  const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

  useEffect(() => { checkAuth(); }, []);

  const checkAuth = async () => {
    const token = getToken();
    if (!token) { navigate('/'); return; }
    try {
      const res = await axios.get(`${API}/auth/me`, getAuthHeaders());
      if (res.data.role !== 'admin' && res.data.role !== 'collaborateur') { toast.error(t('common.loginError')); navigate('/'); return; }
      setUser(res.data); setLoading(false);
      try { await fetchData(res.data.role); } catch (fetchErr) { console.error('fetchData error (non-blocking):', fetchErr); }
    } catch (error) { localStorage.removeItem('briouso_token'); navigate('/'); }
  };

  const fetchData = async (role) => {
    const [releasesRes, sitemapRes] = await Promise.all([
      axios.get(`${API}/config/releases`, getAuthHeaders()),
      axios.get(`${API}/config/sitemap`, getAuthHeaders())
    ]);
    setReleases(releasesRes.data); setSitemap(sitemapRes.data);

    if (role === 'admin') {
      const [usersRes, configRes] = await Promise.all([
        axios.get(`${API}/auth/users`, getAuthHeaders()),
        axios.get(`${API}/config/site`, getAuthHeaders())
      ]);
      setUsers(usersRes.data); setSiteConfig(configRes.data); setConfigForm(configRes.data);
    }

    const [eventsRes, newsRes, newsletterRes] = await Promise.all([
      axios.get(`${API}/events`), axios.get(`${API}/news`),
      axios.get(`${API}/newsletter/subscribers`, getAuthHeaders())
    ]);
    setStats({ events: eventsRes.data.length, news: newsRes.data.length, subscribers: newsletterRes.data.length });
  };

  const handleLogout = () => { localStorage.removeItem('briouso_token'); toast.info(t('common.logoutSuccess')); navigate('/'); };

  const handleCreateRelease = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/config/releases`, newRelease, getAuthHeaders());
      toast.success(t('common.savedSuccess')); setNewRelease({ version: '', title: '', description: '' });
      const res = await axios.get(`${API}/config/releases`, getAuthHeaders()); setReleases(res.data);
    } catch (error) { toast.error(t('common.errorCreating')); }
  };

  const handleUpdateConfig = async (e) => {
    e.preventDefault();
    const allowedKeys = ['smtp_host', 'smtp_port', 'smtp_user', 'smtp_password', 'smtp_from_email', 'contact_recipient_email', 'mailchimp_api_key', 'mailchimp_list_id', 'brevo_api_key'];
    const cleanConfig = {};
    allowedKeys.forEach(key => {
      const val = configForm[key];
      if (val !== undefined && val !== null) {
        if (typeof val === 'string' && val.startsWith('***')) return;
        if (typeof val === 'string' && val.trim() === '') return;
        cleanConfig[key] = val;
      }
    });
    try {
      await axios.put(`${API}/config/site`, cleanConfig, getAuthHeaders());
      toast.success(t('common.savedSuccess'));
    } catch (error) { toast.error(error.response?.data?.detail || t('common.errorUpdating')); }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/register`, newUser, getAuthHeaders());
      toast.success(t('common.savedSuccess')); setNewUser({ email: '', password: '', first_name: '', last_name: '', role: 'adherent', phone: '' });
      const res = await axios.get(`${API}/auth/users`, getAuthHeaders()); setUsers(res.data);
    } catch (error) { toast.error(error.response?.data?.detail || t('common.errorCreating')); }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm(t('common.confirmDeleteUser'))) return;
    try {
      await axios.delete(`${API}/auth/users/${userId}`, getAuthHeaders());
      toast.success(t('common.savedSuccess')); setUsers(users.filter(u => u.id !== userId));
    } catch (error) { toast.error(t('common.errorDeleting')); }
  };

  const handleSendTestEmail = async (e) => {
    e.preventDefault();
    if (!testEmail) { toast.error(t('common.errorOccurred')); return; }
    setSendingTest(true);
    try {
      const res = await axios.post(`${API}/test-email`, { to_email: testEmail }, getAuthHeaders());
      toast.success(res.data.message);
    } catch (error) { toast.error(error.response?.data?.detail || t('common.errorSending')); }
    finally { setSendingTest(false); }
  };

  const toggleUserActive = async (userId, currentStatus) => {
    try {
      await axios.patch(`${API}/auth/users/${userId}`, { is_active: !currentStatus }, getAuthHeaders());
      toast.success(t('common.savedSuccess')); setUsers(users.map(u => u.id === userId ? { ...u, is_active: !currentStatus } : u));
    } catch (error) { toast.error(t('common.errorUpdating')); }
  };

  if (loading) return <div className="min-h-screen pt-24 flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fuchsia-600"></div></div>;
  if (!user) return null;

  const isAdmin = user.role === 'admin';

  return (
    <div className="min-h-screen pt-24 pb-12 bg-gradient-to-br from-zinc-50 to-fuchsia-50/20" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex justify-between items-start mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Shield className="h-8 w-8 text-fuchsia-600" />
              <h1 className="font-['Playfair_Display'] text-3xl font-bold text-zinc-900">Administration</h1>
            </div>
            <p className="text-zinc-600">
              Bienvenue, {user.first_name} {user.last_name}
              <span className={`ml-2 px-2 py-1 text-xs rounded-full ${user.role === 'admin' ? 'bg-fuchsia-100 text-fuchsia-700' : 'bg-lime-100 text-lime-700'}`}>
                {user.role === 'admin' ? 'Administrateur' : 'Collaborateur'}
              </span>
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/')} className="rounded-full"><Home className="h-4 w-4 mr-2" />Accueil</Button>
            <Button variant="outline" onClick={handleLogout} className="rounded-full text-red-600 hover:text-red-700"><LogOut className="h-4 w-4 mr-2" />Déconnexion</Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className={`grid w-full max-w-2xl ${isAdmin ? 'grid-cols-5' : 'grid-cols-3'} bg-white shadow-sm h-auto p-1 rounded-xl mb-8`}>
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-fuchsia-600 data-[state=active]:text-white py-3 rounded-lg"><BarChart3 className="h-4 w-4 mr-2" />Dashboard</TabsTrigger>
            <TabsTrigger value="sitemap" className="data-[state=active]:bg-fuchsia-600 data-[state=active]:text-white py-3 rounded-lg"><FileText className="h-4 w-4 mr-2" />Cartographie</TabsTrigger>
            <TabsTrigger value="releases" className="data-[state=active]:bg-fuchsia-600 data-[state=active]:text-white py-3 rounded-lg"><Bell className="h-4 w-4 mr-2" />Releases</TabsTrigger>
            {isAdmin && <>
              <TabsTrigger value="users" className="data-[state=active]:bg-fuchsia-600 data-[state=active]:text-white py-3 rounded-lg"><Users className="h-4 w-4 mr-2" />Utilisateurs</TabsTrigger>
              <TabsTrigger value="config" className="data-[state=active]:bg-fuchsia-600 data-[state=active]:text-white py-3 rounded-lg"><Settings className="h-4 w-4 mr-2" />Config</TabsTrigger>
            </>}
          </TabsList>

          <TabsContent value="dashboard"><AdminDashboard stats={stats} isAdmin={isAdmin} setActiveTab={setActiveTab} /></TabsContent>
          <TabsContent value="sitemap"><AdminSitemap sitemap={sitemap} /></TabsContent>
          <TabsContent value="releases"><AdminReleases releases={releases} isAdmin={isAdmin} newRelease={newRelease} setNewRelease={setNewRelease} handleCreateRelease={handleCreateRelease} /></TabsContent>
          {isAdmin && <TabsContent value="users"><AdminUsers users={users} newUser={newUser} setNewUser={setNewUser} handleCreateUser={handleCreateUser} handleDeleteUser={handleDeleteUser} toggleUserActive={toggleUserActive} /></TabsContent>}
          {isAdmin && <TabsContent value="config"><AdminConfig configForm={configForm} setConfigForm={setConfigForm} handleUpdateConfig={handleUpdateConfig} testEmail={testEmail} setTestEmail={setTestEmail} sendingTest={sendingTest} handleSendTestEmail={handleSendTestEmail} /></TabsContent>}
        </Tabs>
      </div>
    </div>
  );
}
